import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { AgentConnection } from 'types/agents';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';

const mockConnections: AgentConnection[] = [
    {
        id: 'conn-001',
        name: 'Salesforce OAuth',
        description: 'OAuth connection for CRM workflows',
        createdAt: '2024-02-12T11:22:00Z',
        updatedAt: '2024-03-01T08:10:00Z',
        type: 'oauth',
        settings: {
            provider: 'salesforce',
            scopes: 'read,write',
            environment: 'production',
        },
    },
    {
        id: 'conn-002',
        name: 'Zendesk API',
        description: 'Bearer token for Zendesk ticketing',
        createdAt: '2024-01-05T14:45:00Z',
        updatedAt: '2024-02-20T09:30:00Z',
        type: 'bearer',
        settings: {
            baseUrl: 'https://example.zendesk.com',
            expiresAt: '2024-06-01T00:00:00Z',
            rateLimit: 1200,
        },
    },
    {
        id: 'conn-003',
        name: 'Internal Database',
        description: 'Basic auth for internal reporting DB',
        createdAt: '2023-12-19T07:15:00Z',
        updatedAt: '2024-01-10T12:00:00Z',
        type: 'basic',
        settings: {
            host: '10.0.3.15',
            port: '5432',
            username: 'reporter',
        },
    },
];

const Connections: React.FC = () => {
    const [connections] = useState<AgentConnection[]>(mockConnections);

    const emptyState = connections.length === 0;

    // const summary = useMemo(() => {
    //     const total = connections.length;
    //     const byType = connections.reduce<Record<string, number>>((acc, connection) => {
    //         acc[connection.type] = (acc[connection.type] || 0) + 1;
    //         return acc;
    //     }, {});

    //     return { total, byType };
    // }, [connections]);

    const formatDate = (date: string) =>
        new Date(date).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap justify-between gap-4 items-start">
                <div>
                    <h2 className="text-2xl font-semibold text-foreground">
                        Connections
                    </h2>
                    <p className="text-sm mt-1 text-muted-foreground">
                        Configure and manage third-party service credentials for your workflows.
                    </p>
                </div>
                <button
                    className="px-4 py-2 rounded-md transition-colors flex items-center bg-primary text-primary-foreground hover:opacity-90"
                    onClick={() => console.log('Add new connection')}
                >
                    <Plus size={16} className="mr-2" />
                    New Connection
                </button>
            </div>


            <div
                className="overflow-x-auto rounded-lg shadow bg-background"
            >
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="text-primary">Name</TableHead>
                            <TableHead className="text-primary">Description</TableHead>
                            <TableHead className="text-primary">Type</TableHead>
                            <TableHead className="text-primary">Created</TableHead>
                            <TableHead className="text-primary">Updated</TableHead>
                            <TableHead className="text-primary">Settings</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {emptyState ? (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center py-12 text-sm text-muted-foreground">
                                    No connections are configured yet. Use the “New Connection” button to add one.
                                </TableCell>
                            </TableRow>
                        ) : (
                            connections.map((connection) => (
                                <TableRow
                                    key={connection.id}
                                    className="hover:bg-muted/10 transition-colors"
                                >
                                    <TableCell className="text-foreground">
                                        <div className="font-medium">{connection.name}</div>
                                        <div className="text-xs text-muted-foreground">
                                            {connection.id}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {connection.description || '—'}
                                    </TableCell>
                                    <TableCell>
                                        <span
                                            className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-primary/10 text-primary"
                                        >
                                            {connection.type}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {formatDate(connection.createdAt)}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {formatDate(connection.updatedAt)}
                                    </TableCell>
                                    <TableCell>
                                        {Object.keys(connection.settings).length === 0 ? (
                                            <span className="text-sm text-muted-foreground">
                                                No settings
                                            </span>
                                        ) : (
                                            <div className="space-y-1 text-xs font-mono">
                                                {Object.entries(connection.settings).map(([key, value]) => (
                                                    <div key={key} className="flex justify-between gap-4">
                                                        <span className="text-muted-foreground">{key}</span>
                                                        <span className="text-foreground">{String(value)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};

export default Connections;

